import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import RetirementIncomeDonut, {
  CHART_WIDTH_SINGLE,
  CHART_WIDTH_DUAL,
  CHART_HEIGHT_SINGLE_CAPTION,
  CHART_HEIGHT_DUAL_CAPTION,
  ARC_CAPTION_LAYOUT,
} from '../RetirementIncomeDonut'
import { numberToDollars, roundPercent } from '../../utils/utils'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../models/Person'
import { MOBILE_VIEWPORT } from '../../guided-toolbox/utils/breakpoints'
import { FONT_FAMILY, BOX_PAIR_MAX_WIDTH, BOX_GAP } from './shared'
import { AdviceColumnHeading, AdviceMobileHeading } from '../advice/shared'

const replacementLabel = percent => `${roundPercent(percent)}% of today's income`

const replacementFigureText = ({ amount, percent }) =>
  `$${numberToDollars(amount, true)} (${roundPercent(percent)}%)`

const MobileArcContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

// Match the value rows' two-column layout so each donut aligns with its value box below.
const DesktopPair = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  align-items: start;
  column-gap: ${BOX_GAP}px;
  width: 100%;
  max-width: ${BOX_PAIR_MAX_WIDTH}px;
  margin: 0 auto;
`

const ArcColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 auto;
  row-gap: 6px;

  @media ${MOBILE_VIEWPORT} {
    row-gap: 0;
  }
`

const MobileHeading = styled(AdviceMobileHeading)`
  margin-bottom: 6px;
`

// Bottom-align the donuts so Current aligns with the inner ring of the taller modified donut.
const DonutBox = styled.div`
  flex: 0 0 auto;
  max-width: 100%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`

const ReplacementLine = styled.div`
  font-family: ${FONT_FAMILY};
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  text-align: center;
  color: ${p => p.theme.primaryColor};
`

const MobileFigures = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  margin-top: 20px;
  font-family: ${FONT_FAMILY};
  font-size: 16px;
  line-height: 1.5;
  color: ${p => p.theme.lightestGray};
`

const Figure = styled.div`
  text-align: center;
  font-weight: ${p => (p.bold ? 700 : 400)};
`

const percentCaption = percent => `${roundPercent(percent)}%`

const DesktopArcs = ({ current, recommended, modified, goalPercent }) => {
  const isModified = !!modified

  // Use the taller dual-donut height for both columns so the charts align.
  const boxHeight = isModified ? CHART_HEIGHT_DUAL_CAPTION : CHART_HEIGHT_SINGLE_CAPTION

  return (
    <DesktopPair>
      <ArcColumn>
        <AdviceColumnHeading>Current</AdviceColumnHeading>
        <DonutBox style={{ width: CHART_WIDTH_SINGLE, height: boxHeight, maxWidth: 'none' }}>
          <RetirementIncomeDonut
            animate
            amount={current.amount}
            replacementPercent={current.percent}
            goalPercent={goalPercent}
            subLabel={percentCaption(current.percent)}
            layout={ARC_CAPTION_LAYOUT}
          />
        </DonutBox>
      </ArcColumn>

      <ArcColumn>
        <AdviceColumnHeading planned>
          {isModified ? 'Your changes' : 'Recommended'}
        </AdviceColumnHeading>
        {/* Let the dual donut exceed its grid column so it stays the same scale as Current. */}
        <DonutBox
          style={{
            width: isModified ? CHART_WIDTH_DUAL : CHART_WIDTH_SINGLE,
            height: boxHeight,
            maxWidth: 'none',
          }}>
          {isModified ? (
            <RetirementIncomeDonut
              animate
              amount={modified.amount}
              replacementPercent={modified.percent}
              recommendedPercent={recommended.percent}
              subLabel={percentCaption(modified.percent)}
              layout={ARC_CAPTION_LAYOUT}
              goalCallout={{
                label: 'Our recommendation:',
                amount: recommended.amount,
                percent: roundPercent(recommended.percent),
              }}
            />
          ) : (
            <RetirementIncomeDonut
              animate
              amount={recommended.amount}
              replacementPercent={recommended.percent}
              goalPercent={goalPercent}
              subLabel={percentCaption(recommended.percent)}
              layout={ARC_CAPTION_LAYOUT}
            />
          )}
        </DonutBox>
      </ArcColumn>
    </DesktopPair>
  )
}

const MobileArcs = ({ current, recommended, modified }) => {
  const isModified = !!modified
  const displayedPlan = modified || recommended

  return (
    <MobileArcContainer>
      <ArcColumn>
        <MobileHeading>{isModified ? 'Your changes' : 'Recommended'}</MobileHeading>
        <DonutBox style={{ width: isModified ? CHART_WIDTH_DUAL : CHART_WIDTH_SINGLE }}>
          {isModified ? (
            <RetirementIncomeDonut
              animate
              amount={modified.amount}
              replacementPercent={modified.percent}
              recommendedPercent={recommended.percent}
            />
          ) : (
            <RetirementIncomeDonut
              animate
              amount={recommended.amount}
              replacementPercent={recommended.percent}
              // Hide the goal band in the recommended-only mobile view.
              goalPercent={0}
            />
          )}
        </DonutBox>

        <ReplacementLine>{replacementLabel(displayedPlan.percent)}</ReplacementLine>

        {/* Show the recommendation below because its callout does not fit inside the mobile donut. */}
        <MobileFigures>
          {isModified && (
            <Figure bold>{`Our recommendation: ${replacementFigureText(recommended)}`}</Figure>
          )}
          <Figure>{`Current: ${replacementFigureText(current)}`}</Figure>
        </MobileFigures>
      </ArcColumn>
    </MobileArcContainer>
  )
}

/**
 * Retirement-income comparison shown at the top of the summary page.
 */
const AdviceSummaryArcs = ({
  current,
  recommended,
  modified,
  goalPercent = DEFAULT_RETIREMENT_INCOME_GOAL_PCT,
  className,
}) => {
  const isMobile = useMediaQuery(MOBILE_VIEWPORT)

  return (
    <div className={className}>
      {isMobile ? (
        <MobileArcs current={current} recommended={recommended} modified={modified} />
      ) : (
        <DesktopArcs
          current={current}
          recommended={recommended}
          modified={modified}
          goalPercent={goalPercent}
        />
      )}
    </div>
  )
}

// `amount` is in dollars; `percent` is a 0–100 income-replacement value.
const incomeCasePropType = PropTypes.shape({
  amount: PropTypes.number.isRequired,
  percent: PropTypes.number.isRequired,
})

AdviceSummaryArcs.propTypes = {
  current: incomeCasePropType.isRequired,
  /** Shown as the plan unless modified is provided, then used for comparison. */
  recommended: incomeCasePropType.isRequired,
  /** User-modified plan; omit when the recommendation has not been changed. */
  modified: incomeCasePropType,
  /** Retirement-income goal percentage from 0 to 100. */
  goalPercent: PropTypes.number,
  className: PropTypes.string,
}

export default AdviceSummaryArcs
